import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { IWalletCategory } from '~/@api/generated-api-types-user/Api'
import { IProjectStatus } from '~/@api/generated-api-types-project/Api'
import { Table, TableBody, TableHeader, TableRow } from '~/shadcn/ui/table'
import { Spinner } from '~/shadcn/ui/spinner'
import { NoRecords } from '~/@mid_ui/no-records/NoRecords'
import { useIsDesktop } from '~/@mid_ui/layout/useIsDesktop'
import { FilterToggleButton } from '~/@mid_ui/table/FiltersToggle'
import { getColumnFilterLength } from '~/@mid_ui/table/getColumnFilterLength'
import { PageContainer } from '~/@mid_ui/page-container/PageContainer'
import { FiltersContainer } from '~/@mid_ui/table/FiltersContainer'
import { PAGE_SIZE } from '~/@mid_query/utils/pagination.utils'
import { useUser } from '~/@biz_account/useUser/useUser'
import {
  ProjectsTab,
  ProjectsTabsContainer,
} from '../_projects_filtering/ProjectsTab'
import { useFilters } from '../archived-projects_filters/useFilters'
import { projectsFilteredForCurrentTab } from '../archived-projects_filters/utils'
import { ArchivedProjectsFilters } from '../archived-projects_filters/ArchivedProjectsFilters'
import { getArchivedProjectsTabs } from '../archived-projects_filters/getArchivedProjectsTabs'
import type { ProjectsTableMeta } from '../running-projects/useRunningProjectsTable'
import { useArchivedProjectsTable } from './useArchivedProjectsTable'
import { useArchivedProjectsQuery } from './useArchivedProjectsQuery'
import { ProjectsTableHead } from '../_projects_ui/project-table-components/ProjectsTableHead'
import { ProjectsTableRow } from '../_projects_ui/project-table-components/ProjectsTableRow'
import { Button } from '~/shadcn/ui/button/button'

export function ArchivedProjectsTable() {
  const {
    selectedTab,
    setSelectedTab,

    isFilterOpen,
    toggleFilterOpen,

    sorting,
    handleSortingChange,
  } = useFilters()

  const { data, isFetching: isLoading } = useArchivedProjectsQuery()

  const projects = (() => data?.items ?? [])()

  const navigate = useNavigate()
  const { user: userWithMeta } = useUser()
  const isDesktop = useIsDesktop()

  const projectsTabs = useMemo(() => {
    const fundedCount = projects.filter(
      (project) => project.statusId === IProjectStatus.Funded
    ).length
    const finishedCount = projects.filter(
      (project) => project.statusId === IProjectStatus.Finished
    ).length

    return getArchivedProjectsTabs(fundedCount, finishedCount)
  }, [projects])

  const projectsForCurrentTab = useMemo(() => {
    return projectsFilteredForCurrentTab(selectedTab, projects)
  }, [selectedTab, projects])

  const table = useArchivedProjectsTable(
    projectsForCurrentTab,
    {
      sorting,
      onSortingChange: handleSortingChange,
    },
    {
      userMeta: userWithMeta?.['meta'],
      investorWallet: userWithMeta?.['wallets'].find(
        ({ category }) => category === IWalletCategory.Investor
      ),
    } as ProjectsTableMeta
  )

  const columnFiltersLength = getColumnFilterLength(table)
  const activeFiltersCount = columnFiltersLength
    ? ` (${columnFiltersLength})`
    : ''

  if (isLoading && projects.length === 0) {
    return (
      <div className="flex justify-center items-center py-4 md:py-7">
        <Spinner className="size-6" />
      </div>
    )
  }

  return (
    <div translate="no">
      <ProjectsTabsContainer>
        <PageContainer className="flex items-center justify-between">
          <div className="flex overflow-y-auto">
            {projectsTabs.map((tab, index) => (
              <ProjectsTab
                colorScheme="light"
                key={`RunningProjectsTab-${index}`}
                isActive={selectedTab === index}
                onClick={() => {
                  setSelectedTab(index)
                }}
                {...tab}
              />
            ))}
          </div>

          {isDesktop && !!projectsForCurrentTab.length && (
            <FilterToggleButton
              toggleFilterOpen={toggleFilterOpen}
              isFilterOpen={isFilterOpen}
              activeFiltersCount={activeFiltersCount}
            />
          )}
        </PageContainer>
      </ProjectsTabsContainer>

      {!isDesktop && !!projectsForCurrentTab.length && (
        <div className="border-b border-border">
          <PageContainer>
            <FilterToggleButton
              toggleFilterOpen={toggleFilterOpen}
              isFilterOpen={isFilterOpen}
              activeFiltersCount={activeFiltersCount}
            />
          </PageContainer>
        </div>
      )}
      {isFilterOpen && (
        <FiltersContainer colorScheme="light">
          <PageContainer>
            <ArchivedProjectsFilters colorScheme="light" table={table} />
          </PageContainer>
        </FiltersContainer>
      )}

      <PageContainer className="pb-15 md:pb-30">
        {projectsForCurrentTab.length === 0 && (
          <NoRecords className="my-20" message="Žiadne záznamy" />
        )}
        {projectsForCurrentTab.length > 0 && (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow
                      key={`TableRow-${headerGroup.id}`}
                      className="border-b border-border">
                      {headerGroup.headers.map((header, index) => (
                        <ProjectsTableHead
                          key={`TableHead-${header.id}`}
                          header={header}
                          index={index}
                          usePrimaryForeground={false}
                          useCustomIconColors={false}
                          colorScheme="light"
                        />
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>

                <TableBody className="h-20 border-b border-border">
                  {table.getRowModel().rows.map((row, index) => (
                    <ProjectsTableRow
                      key={`TableRow-${row.id}`}
                      row={row}
                      userWithMeta={userWithMeta}
                      navigate={navigate}
                      enableAdvancedStyles
                      rowIndex={index}
                      colorScheme="light"
                    />
                  ))}
                </TableBody>
              </Table>
            </div>

            {table.getCanNextPage() && (
              <div className="flex items-center justify-center pt-7 md:pt-10">
                <Button
                  variant="outline"
                  onClick={() =>
                    table.setPageSize((pageSize) => pageSize + PAGE_SIZE)
                  }>
                  Ukázať ďalšie príležitosti
                </Button>
              </div>
            )}
          </>
        )}
      </PageContainer>
    </div>
  )
}
